import { getSiloV2MarketEntry, siloV2LenderKey } from '@1delta/data-sdk'
import type { AdditionalYields } from '../../../types'
import type { GenericTokenList } from '../../../types/general'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { createMarketUid } from '../../../utils'
import type { SiloApiEntry } from './fetchPublic'

/**
 * Convert a Silo GraphQL `silos.items` response into the same per-pair
 * shape emitted by the on-chain Silo v2 parser
 * (`getSiloV2ReservesDataConverter`).
 *
 * Per API silo entry we emit exactly one per-pair lender key
 * (`SILO_V2_<configAddress>`) carrying BOTH sides of the pair as reserve
 * entries, keyed by `createMarketUid(chainId, lender, siloAddress)`.
 *
 * The API's dynamic fields (`supply`, `borrowed`, `borrowRate`,
 * `depositRate`, `supplyUsd`, `borrowedUsd`) are decimal strings already
 * denominated in token units — we consume them directly without any wei /
 * 1e18 shifting. `lt` and `maxLtv` stay as 1e18-scaled bigint strings.
 *
 * Static metadata the API does not expose (protected share token, per-side
 * IRM address unless present, hook receiver) is pulled from the data-sdk
 * registry via `getSiloV2MarketEntry`. If the registry has no entry for a
 * given `configAddress` we skip it — the on-chain path owns discovery.
 */
export function convertSiloMarketsToPublicResponse(
  apiItems: SiloApiEntry[],
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokenList: GenericTokenList = {},
): { [lender: string]: any } {
  const out: { [lender: string]: any } = {}

  for (const item of apiItems) {
    // Silo v2 lender keys are built off the shared siloConfig.
    if (item.protocol && item.protocol.protocolVersion !== 'v2') continue

    let lenderKey: string
    try {
      lenderKey = siloV2LenderKey(item.configAddress)
    } catch {
      continue
    }

    const registryEntry = getSiloV2MarketEntry(chainId, lenderKey)
    if (!registryEntry) continue

    // Align API market1/market2 to registry silo0/silo1 by `market.index`.
    // Silo v2 emits market.index ∈ {0,1} mirroring SiloConfig's silo0/silo1.
    const [apiSide0, apiSide1] =
      item.market1.index === 0
        ? [item.market1, item.market2]
        : [item.market2, item.market1]

    const sides = [
      { api: apiSide0, self: registryEntry.silo0, other: registryEntry.silo1 },
      { api: apiSide1, self: registryEntry.silo1, other: registryEntry.silo0 },
    ]

    const data: { [marketUid: string]: any } = {}

    for (const { api, self, other } of sides) {
      const decimals = api.inputToken?.decimals ?? self.decimals
      const tokenAddrLc = (api.inputTokenId ?? self.token).toLowerCase()

      // The indexer's `supply` aggregates Collateral + Protected. Only
      // Collateral backs borrows, so we read the Collateral-only fields
      // for the borrow-pool math (matches the on-chain path's
      // `Silo.totalAssets()` which is Collateral-only). Fall back to
      // `supply` / derived liquidity / derived utilization if the
      // indexer omits the explicit fields for a given market.
      const totalDeposits =
        api.collateralAssetsSupply != null
          ? safeNumber(api.collateralAssetsSupply)
          : safeNumber(api.supply)
      const totalDebt = safeNumber(api.borrowed)
      const totalLiquidity =
        api.liquidity != null
          ? safeNumber(api.liquidity)
          : Math.max(totalDeposits - totalDebt, 0)
      const utilization =
        api.utilization != null
          ? safeNumber(api.utilization)
          : totalDeposits > 0
            ? totalDebt / totalDeposits
            : 0

      // `borrowRate` / `depositRate` are returned by the indexer already as
      // percent values (e.g. "38.91" = 38.91 %), matching the rest of the
      // fetcher's APR convention — consume them directly.
      const variableBorrowRatePct = safeNumber(api.borrowRate)
      const depositRatePct = safeNumber(api.depositRate)

      const assetMeta = tokenList[tokenAddrLc]
      const oracleKey =
        toOracleKey(assetMeta?.assetGroup ?? null) ||
        toGenericPriceKey(tokenAddrLc, chainId)
      // Prefer the indexer's Collateral-only USD figure (matches the
      // Collateral-only `totalDeposits` above); fall back to its full
      // `supplyUsd`, then to the external price map × token amount.
      const fallbackPrice = prices[oracleKey] ?? 0
      const totalDepositsUSD =
        api.collateralAssetsSupplyUsd != null && api.collateralAssetsSupplyUsd > 0
          ? api.collateralAssetsSupplyUsd
          : api.supplyUsd != null && api.supplyUsd > 0
            ? api.supplyUsd
            : totalDeposits * fallbackPrice
      const totalDebtUSD =
        api.borrowedUsd != null && api.borrowedUsd > 0
          ? api.borrowedUsd
          : totalDebt * fallbackPrice
      const totalLiquidityUSD =
        api.liquidityUsd != null && api.liquidityUsd > 0
          ? api.liquidityUsd
          : Math.max(totalDepositsUSD - totalDebtUSD, 0)

      // In Silo v2 each silo's own lt/maxLtv describe how its DEPOSITS work
      // as collateral when borrowing the OTHER silo's asset. So:
      //  - this asset is collateral-usable iff selfLt > 0
      //  - this asset is borrowable iff the OTHER side has lt > 0 (i.e.
      //    someone can post collateral against which to borrow it)
      // A side with lt == 0 is the "borrow-only" half of an asymmetric pair.
      const otherApi = sides[0].api === api ? apiSide1 : apiSide0
      const selfLt = bigintToLtv(api.lt ?? self.lt)
      const selfMaxLtv = bigintToLtv(api.maxLtv ?? self.maxLtv)
      const otherLt = bigintToLtv(otherApi.lt ?? other.lt)

      // `liquidationFee` is the seize penalty, WAD-scaled (1e18 = 100%) —
      // same scale as lt/maxLtv. Normalize to a plain fraction.
      const selfLiquidationPenalty = bigintToLtv(
        api.liquidationFee ?? self.liquidationFee,
      )

      const collateralActive = selfLt > 0
      const borrowingEnabled = otherLt > 0

      const siloAddrLc = self.silo.toLowerCase()
      const marketUid = createMarketUid(chainId, lenderKey, siloAddrLc)

      data[marketUid] = {
        marketUid,
        name: item.name
          ? `Silo V2 ${item.name} ${assetMeta?.symbol ?? api.inputToken?.symbol ?? ''}`
          : `Silo V2 ${assetMeta?.symbol ?? api.inputToken?.symbol ?? ''}`,
        poolId: siloAddrLc,
        underlying: tokenAddrLc,
        asset: {
          ...(assetMeta ?? {}),
          chainId,
          decimals,
          address: tokenAddrLc,
          symbol: assetMeta?.symbol ?? api.inputToken?.symbol ?? self.symbol ?? '',
          name: assetMeta?.name ?? api.inputToken?.symbol ?? self.symbol ?? '',
        },
        // token amounts
        totalDeposits,
        totalDebt,
        totalDebtStable: 0,
        totalLiquidity,
        borrowLiquidity: totalLiquidity,
        // USD amounts
        totalDepositsUSD,
        totalDebtUSD,
        totalDebtStableUSD: 0,
        totalLiquidityUSD,
        borrowLiquidityUSD: totalLiquidityUSD,
        utilization,
        depositRate: depositRatePct,
        variableBorrowRate: variableBorrowRatePct,
        stableBorrowRate: 0,
        intrinsicYield:
          additionalYields?.intrinsicYields?.[assetMeta?.assetGroup as any] ??
          0,
        rewards: undefined,
        decimals,
        config: {
          0: {
            category: 0,
            borrowCollateralFactor: selfMaxLtv,
            collateralFactor: selfLt,
            borrowFactor: 1,
            liquidationPenalty: selfLiquidationPenalty,
            closeFactor: 1,
            collateralDisabled: !collateralActive,
            debtDisabled: !borrowingEnabled,
          },
        },
        closeFactor: 1,
        collateralActive,
        borrowingEnabled,
        depositsEnabled: true,
        hasStable: false,
        isActive: true,
        isFrozen: false,
        params: {
          metadata: {
            silo: self.silo.toLowerCase(),
            counterpartySilo: other.silo.toLowerCase(),
            siloConfig: registryEntry.siloConfig.toLowerCase(),
            oracle: (
              api.solvencyOracleAddress ?? self.solvencyOracle
            ).toLowerCase(),
            irm: (
              api.interestRateModelId ?? self.interestRateModel
            ).toLowerCase(),
            shareTokens: {
              collateral: self.collateralShareToken,
              protected: self.protectedShareToken,
              debt: self.debtShareToken,
            },
            fees: {
              dao: api.daoFee ?? self.daoFee,
              deployer: api.deployerFee ?? self.deployerFee,
              liquidation: api.liquidationFee ?? self.liquidationFee,
              flashloan: api.flashLoanFee ?? self.flashloanFee,
            },
          },
        },
      }
    }

    // Each SILO_V2_<HEX> lender carries the two-sided pair result. The
    // `chainId` field keeps parity with the on-chain parser's output.
    out[lenderKey] = { data, chainId }
  }

  return out
}

function safeNumber(v: string | number | null | undefined): number {
  if (v == null) return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

function bigintToLtv(v: string | null | undefined): number {
  if (!v) return 0
  try {
    return Number(BigInt(v)) / 1e18
  } catch {
    const n = Number(v)
    return Number.isFinite(n) ? n / 1e18 : 0
  }
}

